import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../AxiosInstance';
import { trackProductDeleted } from '../services/analytics';
import { getBestProductImage, productHasImage } from '../utils/productImages';
import { Toast, Modal, DetailRow, PageHeader, SearchBar, Pagination, DeleteConfirmModal, LoadingSpinner, EmptyState } from './v2-utils';
import '../styles/v2.css';

const PAGE_SIZE = 6;

function getInitials(name) {
  if (!name) return '??';
  return name.split(/[\s\-()\[\]]+/).filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

const GRAD = ['linear-gradient(135deg,#f87171,#ef4444)', 'linear-gradient(135deg,#fbbf24,#d97706)', 'linear-gradient(135deg,#60a5fa,#3b82f6)', 'linear-gradient(135deg,#a78bfa,#8b5cf6)', 'linear-gradient(135deg,#34d399,#10b981)', 'linear-gradient(135deg,#f472b6,#ec4899)', 'linear-gradient(135deg,#fb923c,#ea580c)'];

function ProductCard({ product, idx, onEdit, onDelete, onInfo, onToggleFavorite, isOnline }) {
  const imgSrc = getBestProductImage(product, 'thumbnail');
  const hasImg = productHasImage(product);
  const isFav  = product.is_favorite === 1 || product.is_favorite === true;

  return (
    <div className="v2-card" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
      {/* Image or placeholder */}
      <div style={{ height: 140, background: hasImg ? '#f8fafc' : GRAD[idx % GRAD.length], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative' }}>
        {hasImg ? (
          <img src={imgSrc} alt={product.product_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span style={{ color: 'white', fontSize: 28, fontWeight: 900 }}>{getInitials(product.product_name)}</span>
        )}
        {/* Fav button */}
        <button onClick={() => onToggleFavorite(product)} disabled={!isOnline}
          style={{ position: 'absolute', top: 8, right: 8, width: 32, height: 32, borderRadius: '50%', background: isFav ? '#fef3c7' : 'rgba(255,255,255,0.9)', border: 'none', cursor: isOnline ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
          <svg style={{ width: 16, height: 16, color: isFav ? '#f59e0b' : '#94a3b8', fill: isFav ? '#f59e0b' : 'none' }} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.562.562 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
          </svg>
        </button>
        {/* Image badge */}
        <div style={{ position: 'absolute', bottom: 8, left: 8, padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700, background: hasImg ? 'rgba(16,185,129,0.9)' : 'rgba(100,116,139,0.7)', color: 'white' }}>
          {hasImg ? 'Has Image' : 'No Image'}
        </div>
      </div>

      {/* Info */}
      <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {product.product_name}{product.brand ? ` (${product.brand})` : ''}
        </p>
        {(product.variety || product.size) && (
          <p style={{ margin: 0, fontSize: 12, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {[product.variety, product.size].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>

      {/* Actions */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f1', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button onClick={() => onEdit(product)} disabled={!isOnline}
          style={{ flex: 1, minWidth: 56, padding: '7px 0', borderRadius: 9, border: '1.5px solid #a7f3d0', background: '#f0fdf4', color: '#065f46', fontSize: 11, fontWeight: 700, cursor: isOnline ? 'pointer' : 'not-allowed', opacity: isOnline ? 1 : 0.5 }}>
          Edit
        </button>
        <button onClick={() => onEdit(product, true)} disabled={!isOnline}
          style={{ flex: 1, minWidth: 56, padding: '7px 0', borderRadius: 9, border: '1.5px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontSize: 11, fontWeight: 700, cursor: isOnline ? 'pointer' : 'not-allowed', opacity: isOnline ? 1 : 0.5 }}>
          {hasImg ? 'Edit Image' : 'Add Image'}
        </button>
        <button onClick={() => onInfo(product)}
          style={{ width: 34, padding: '7px 0', borderRadius: 9, border: '1.5px solid #e2e8f0', background: 'white', color: '#64748b', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" /></svg>
        </button>
        <button onClick={() => onDelete(product)}
          style={{ width: 34, padding: '7px 0', borderRadius: 9, border: '1.5px solid #fecaca', background: '#fef2f2', color: '#ef4444', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
        </button>
      </div>
    </div>
  );
}

export default function ViewProductsV2() {
  const navigate  = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [page, setPage]         = useState(0);
  const [selected, setSelected] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [toast, setToast]       = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const up = () => { setIsOnline(true); setToast({ message: 'Back online.', severity: 'success' }); };
    const down = () => { setIsOnline(false); setToast({ message: 'You are offline. Some actions are disabled.', severity: 'warning' }); };
    window.addEventListener('online', up); window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);

  useEffect(() => {
    axiosInstance.get('/products').then(r => setProducts(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { setPage(0); }, [search]);

  const filtered = products.filter(p => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return `${p.product_name} ${p.brand || ''} ${p.variety || ''} ${p.size || ''}`.toLowerCase().includes(q);
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated  = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const handleEdit = (product, addImage = false) => {
    if (!isOnline) { setToast({ message: 'Cannot edit while offline.', severity: 'warning' }); return; }
    navigate(`/new/dashboard/products/edit/${product.product_id}${addImage ? '?addImage=true' : ''}`);
  };

  const handleToggleFavorite = async (product) => {
    if (!isOnline) { setToast({ message: 'Cannot update favorites while offline.', severity: 'warning' }); return; }
    const newVal = product.is_favorite === 1 || product.is_favorite === true ? 0 : 1;
    try {
      await axiosInstance.post(`/products/${product.product_id}/favorite`, { isFavorite: !!newVal });
      setProducts(prev => prev.map(p => p.product_id === product.product_id ? { ...p, is_favorite: newVal } : p));
    } catch { setToast({ message: 'Failed to update favorite.', severity: 'error' }); }
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/products/${toDelete.product_id}`);
      trackProductDeleted(toDelete.product_id);
      setProducts(prev => prev.filter(p => p.product_id !== toDelete.product_id));
      setToast({ message: 'Product deleted.', severity: 'success' });
    } catch { setToast({ message: 'Failed to delete product.', severity: 'error' }); }
    finally { setToDelete(null); }
  };

  if (loading) return <LoadingSpinner message="Loading products…" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader title="Products" subtitle={`${filtered.length} product${filtered.length !== 1 ? 's' : ''}`}
        action={
          <button onClick={() => navigate('/new/dashboard/products/add')} className="v2-btn-submit"
            style={{ width: 'auto', padding: '10px 20px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg style={{ width: 15, height: 15 }} fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
            Add Product
          </button>
        } />

      <SearchBar value={search} onChange={setSearch} placeholder="Search by name, brand, variety, size…" />

      {paginated.length === 0 ? (
        <EmptyState message={search ? 'No products match your search.' : 'No products added yet.'} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
          {paginated.map((p, i) => (
            <ProductCard key={p.product_id} product={p} idx={i}
              onEdit={handleEdit} onDelete={() => setToDelete(p)}
              onInfo={() => setSelected(p)} onToggleFavorite={handleToggleFavorite}
              isOnline={isOnline} />
          ))}
        </div>
      )}

      <Pagination page={page} totalPages={totalPages} pageSize={PAGE_SIZE} totalItems={filtered.length}
        onPageChange={setPage} onPageSizeChange={() => {}} pageSizes={[PAGE_SIZE]} />

      <Modal open={!!selected} onClose={() => setSelected(null)} title="Product Details"
        footer={<button onClick={() => setSelected(null)} style={{ padding: '9px 20px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: 'white', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Close</button>}>
        {selected && <>
          <DetailRow label="Product Name" value={selected.product_name} />
          <DetailRow label="Brand" value={selected.brand} />
          <DetailRow label="Variety" value={selected.variety} />
          <DetailRow label="Size / Description" value={selected.size || selected.description} />
          <DetailRow label="Has Image" value={productHasImage(selected) ? 'Yes' : 'No'} />
          <DetailRow label="Favourite" value={selected.is_favorite ? 'Yes' : 'No'} />
        </>}
      </Modal>

      <DeleteConfirmModal open={!!toDelete} onClose={() => setToDelete(null)} onConfirm={handleDelete}
        itemDescription={toDelete ? `${toDelete.product_name}${toDelete.brand ? ` (${toDelete.brand})` : ''}` : ''} />

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
